import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { DripSchedule, Mover } from "@/types";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Save, Mail, HelpCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";
// import { Zippy } from "../Zippy";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import {
  faSms,
  faUser,
} from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { dayName, defaultSchedules, DripConfig } from "./constants";

interface DripCampaignsProps {
  leadStatus: "initial" | "contacting" | "quoted";
}

const DripCampaigns: React.FC<DripCampaignsProps> = ({ leadStatus }) => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const [updateMover, { isLoading }] = useUpdateMoverMutation();
  const { toast } = useToast();
  const [schedules, setSchedules] = useState<DripConfig[]>([]);
  const [forStatus, setForStatus] = useState<
    "initial" | "contacting" | "quoted"
  >(leadStatus);
  const [showHelp, setShowHelp] = useState(false);

  const getDefaultSchedules = (
    status: "initial" | "contacting" | "quoted"
  ): DripConfig[] => {
    if (status === "initial" || status === "contacting") {
      return defaultSchedules.new;
    } else {
      return defaultSchedules.quoted;
    }
  };

  useEffect(() => {
    if (
      selectedMover?.drip_schedules &&
      selectedMover.drip_schedules.length > 0
    ) {
      const savedSchedules = selectedMover.drip_schedules.filter(
        (schedule) => schedule.lead_status === forStatus
      );

      const defaults = getDefaultSchedules(forStatus);
      const merged = defaults.map((defaultSchedule) => {
        const existing = savedSchedules.find(
          (s) =>
            s.on_day === defaultSchedule.on_day &&
            s.slot === defaultSchedule.slot &&
            s.channel === defaultSchedule.channel
        );

        return { ...(existing || defaultSchedule), day: defaultSchedule.day };
      });

      setSchedules(merged);
    } else {
      setSchedules(getDefaultSchedules(forStatus));
    }
  }, [selectedMover, forStatus]);

  const updateSchedule = (index: number, updates: Partial<DripSchedule>) => {
    const newSchedules = [...schedules];
    newSchedules[index] = { ...newSchedules[index], ...updates };
    setSchedules(newSchedules);
  };

  const handleSave = async () => {
    if (!selectedMover) return;

    try {
      const updatedMover = await updateMover({
        data: {
          drip_schedules_attributes: schedules.map((schedule) => ({
            ...schedule,
            lead_status: forStatus,
          })),
        },
      }).unwrap();

      // Update local state
      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Drip campaign schedules saved successfully",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to save drip campaign schedules",
        variant: "destructive",
      });
    }
  };

  const tagsHelpContent = (
    <div className="w-full p-4 bg-stone-50 border border-stone-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-base font-bold">Smart Fields</h4>
        <Button
          variant="ghost"
          size="sm"
          className="lg:hidden"
          onClick={() => setShowHelp(false)}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <p className="text-sm text-gray-700 mb-4">
        You can use smart fields to personalize your message. Please use the
        field exactly as shown (including the #).
      </p>
      <ul className="mt-4 list-none p-0 m-0 text-sm space-y-1">
        <li>
          <span className="font-mono tracking-wide font-semibold">
            #customerfullname
          </span>{" "}
          <span className="">Customer's full name</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #customerfirstname
          </span>{" "}
          <span className="">Customer's first name</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #customerhublink
          </span>{" "}
          <span className="">Customer Portal Link</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #companyname
          </span>{" "}
          <span className="">Company name</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #companyphone
          </span>{" "}
          <span className="">Business phone</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #companyemail
          </span>{" "}
          <span className="">Contact email</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">#owner</span>{" "}
          <span className="">Owner's name (first name)</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #salesrep
          </span>{" "}
          <span className="">Name of the sales rep (first name)</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #movedate
          </span>{" "}
          <span className="">Service date (shown as mm/dd/yyyy)</span>
        </li>

        <li>
          <span className="font-mono tracking-wide font-semibold">
            #movecities
          </span>{" "}
          <span className="">From - To City (eg Austin to Dallas)</span>
        </li>
      </ul>
    </div>
  );

  if (!selectedMover) {
    return null;
  }

  // Group schedules by day
  // const schedulesByDay = schedules.reduce((acc, schedule, index) => {
  //   const key = schedule.day;
  //   if (!acc[key]) acc[key] = [];
  //   acc[key].push({ schedule, index });
  //   return acc;
  // }, {} as Record<string, Array<{ schedule: DripSchedule; index: number }>>);

  return (
    <div className="space-y-6 w-full max-w-screen-md p-6 pb-24">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <h2 className="text-xl font-bold">Automated Lead Follow-Ups</h2>
              <h4 className="text-sm text-stone-600 font-medium">
                Manage your automated lead follow-up emails and texts.
              </h4>
            </div>
            {/* <Zippy position="top-right">
              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Note</h3>
                <p className="text-sm text-gray-800">
                  An automated follow-up is only sent if no contact (email/text)
                  has been initiated with the customer in a 5 hour window. Text
                  messages are sent only between 9am-5pm. Please keep this in
                  mind when setting up your follow-up schedule.
                  <br />
                  <br />
                  This is done to reduce spam complaints and prevent customers
                  from unsubscribing.
                </p>
              </div>
            </Zippy> */}
          </div>

          <Button
            variant="link"
            size="sm"
            className="lg:hidden text-stone-800 underline underline-offset-4"
            onClick={() => setShowHelp(!showHelp)}
          >
            <HelpCircle className="h-3 w-3 mr-1" />
            {showHelp ? "Hide Smart Fields" : "Smart Fields"}
          </Button>
        </div>
        <div className="flex items-center gap-8 text-sm border-b border-stone-200">
          <div
            className={cn(
              "cursor-pointer text-stone-500 font-semibold",
              forStatus === "initial" &&
                "text-black font-bold underline underline-offset-4 decoration-brand decoration-2"
            )}
            onClick={() => setForStatus("initial")}
          >
            New Leads
          </div>
          <div
            className={cn(
              "cursor-pointer text-stone-500 font-semibold",
              forStatus === "quoted" &&
                "text-black font-bold underline underline-offset-4 decoration-brand decoration-2"
            )}
            onClick={() => setForStatus("quoted")}
          >
            Quoted Leads
          </div>
        </div>
      </div>

      {/* Mobile help section - shows when toggled */}
      {showHelp && <div className="lg:hidden">{tagsHelpContent}</div>}

      <div className="flex flex-col lg:flex-row-reverse lg:items-start gap-6">
        {/* Desktop help section - always visible */}
        {/* <div className="hidden lg:block">{tagsHelpContent}</div> */}

        <div className="w-full space-y-4">
          {schedules
            .sort((a, b) => a.on_day - b.on_day)
            .map((schedule, index) => (
              <div key={index}>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {schedule.channel === "email" ? (
                      <Mail className="h-4 w-4" />
                    ) : (
                      <FontAwesomeIcon icon={faSms} className="h-4 w-4" />
                    )}
                    <div className="flex items-center gap-1">
                      <span>{schedule.day}</span>
                      <span className="text-sm text-slate-600 font-semibold">
                        ({schedule.channel})
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <DripCampaignsDialog
                      schedule={schedule}
                      index={index}
                      onUpdate={(updates) => updateSchedule(index, updates)}
                    />
                    <MessagePreview schedule={schedule} mover={selectedMover} />
                    <div className="flex items-center gap-1 uppercase text-xs text-slate-500 font-semibold text-nowrap">
                      <span>Off</span>
                      <Switch
                        checked={schedule.enabled}
                        onCheckedChange={(enabled) =>
                          updateSchedule(index, { enabled })
                        }
                      />
                      <span>On</span>
                    </div>
                  </div>
                </div>
                <div className="ml-2 border-l border-slate-200 border-l-2 pl-3 space-y-4 mt-2 w-2/3">
                  <MessageSnippet schedule={schedule} />
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="bg-stone-50 fixed bottom-0 left-0 w-full p-4 border-t border-gray-200">
        <Button onClick={handleSave} disabled={isLoading} size="lg">
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? "Saving..." : "Save Schedules"}
        </Button>
      </div>
    </div>
  );
};

interface DripCampaignsDialogProps {
  schedule: DripSchedule;
  index: number;
  onUpdate: (updates: Partial<DripSchedule>) => void;
}

const DripCampaignsDialog: React.FC<DripCampaignsDialogProps> = ({
  schedule,
  index,
  onUpdate,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [slot, setSlot] = useState(schedule.slot);
  const [editSubject, setEditSubject] = useState(schedule.subject || "");
  const [editBody, setEditBody] = useState(schedule.body || "");

  // Add this useEffect to reset local state when schedule changes
  useEffect(() => {
    setSlot(schedule.slot);
    setEditSubject(schedule.subject || "");
    setEditBody(schedule.body || "");
  }, [schedule]);

  const formatSlot = (slot: string, channel: string): string => {
    if (channel === "email") {
      if (slot === "morning") return "Morning (6am-9am)";
      if (slot === "afternoon") return "Afternoon (2pm-7pm)";
      return "Immediately";
    } else {
      if (slot === "morning") return "Morning (9am-10:30am)";
      if (slot === "afternoon") return "Afternoon (2pm-5pm)";
      return "Immediately";
    }
  };

  const handleClose = () => {
    onUpdate({
      subject: editSubject,
      body: editBody,
      slot: slot || null,
    });

    setIsDialogOpen(false);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-1">
          <FontAwesomeIcon
            icon={faPenToSquare}
            className="h-4 w-4 text-stone-700"
          />
          <span>Edit</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg h-3/4 overflow-y-auto flex flex-col justify-start">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            {schedule.channel === "email" ? (
              <Mail className="h-4 w-4" />
            ) : (
              <FontAwesomeIcon icon={faSms} className="h-4 w-4" />
            )}
            {dayName(schedule.on_day, schedule.slot)} ({schedule.channel})
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            {schedule.slot && (
              <div className="space-y-2">
                <Select
                  value={slot || "morning"}
                  onValueChange={(value) => {
                    setSlot(value as "morning" | "afternoon");
                    onUpdate({ slot: value as "morning" | "afternoon" });
                  }}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">
                      {formatSlot("morning", schedule.channel)}
                    </SelectItem>
                    <SelectItem value="afternoon">
                      {formatSlot("afternoon", schedule.channel)}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-600 font-medium">
                  Tip: emails in the morning and texts in the afternoon work
                  better.
                </div>
              </div>
            )}
          </div>
          {schedule.channel === "email" && (
            <div className="space-y-2">
              <label
                htmlFor={`edit-subject-${index}`}
                className="text-sm font-medium"
              >
                Subject
              </label>
              <Input
                id={`edit-subject-${index}`}
                value={editSubject}
                onChange={(e) => {
                  setEditSubject(e.target.value);
                  onUpdate({ subject: e.target.value });
                }}
                placeholder="Email subject"
              />
            </div>
          )}
          <div className="space-y-2">
            <label
              htmlFor={`edit-body-${index}`}
              className="text-sm font-medium"
            >
              Message
            </label>
            <Textarea
              id={`edit-body-${index}`}
              value={editBody}
              onChange={(e) => {
                setEditBody(e.target.value);
                onUpdate({ body: e.target.value });
              }}
              placeholder={`${
                schedule.channel.charAt(0).toUpperCase() +
                schedule.channel.slice(1)
              } message`}
              rows={8}
            />
          </div>
          <div className="mt-4">
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface MessageSnippetProps {
  schedule: DripSchedule;
}

const MessageSnippet: React.FC<MessageSnippetProps> = ({ schedule }) => {
  const hasContent = schedule.subject || schedule.body;
  const isEmail = schedule.channel === "email";

  return (
    <div className="space-y-2">
      <div className="relative group">
        {hasContent ? (
          <div className="space-x-2 text-stone-800">
            {isEmail && schedule.subject && (
              <span className="text-sm font-semibold">{schedule.subject}</span>
            )}
            {schedule.body && (
              <span
                dangerouslySetInnerHTML={{
                  __html:
                    schedule.body.slice(0, 100).replace(/\n/g, "") + "...",
                }}
                className="text-sm whitespace-pre-wrap"
              />
            )}
          </div>
        ) : (
          <div className="text-gray-400 text-sm">
            {isEmail ? "No subject or message set" : "No message set"}
          </div>
        )}
      </div>
    </div>
  );
};

interface MessagePreviewProps {
  schedule: DripSchedule;
  mover: Mover;
}

const MessagePreview: React.FC<MessagePreviewProps> = ({ schedule, mover }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-1">
          <FontAwesomeIcon icon={faEye} className="h-4 w-4 text-stone-700" />
          <span>Preview</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[90vw] max-w-2xl h-3/4 overflow-y-auto flex flex-col justify-start">
        <DialogHeader className="text-left">
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            {schedule.channel === "email" ? (
              <Mail className="h-4 w-4" />
            ) : (
              <FontAwesomeIcon icon={faSms} className="h-4 w-4" />
            )}
            {dayName(schedule.on_day, schedule.slot)} ({schedule.channel})
          </DialogTitle>
        </DialogHeader>
        {schedule.channel === "email" ? (
          <div>
            <div className="space-y-1">
              <h3 className="text-base font-bold">Email Preview</h3>
              <p className="text-sm text-stone-600">
                This is a preview of the email that will be sent (with actual
                customer data inserted in place of the smart fields).
              </p>
            </div>
            <div className="mt-4 bg-white rounded-lg border border-stone-200">
              <div className="p-4 bg-slate-100">
                {/* <div className="flex items-start gap-2">
                  <span className="text-sm font-semibold">From:</span>
                  <span className="text-sm text-stone-600">
                    {mover?.mover_configuration.sending_email}
                  </span>
                </div> */}
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">To:</span>
                  <span className="text-sm">john.doe@example.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">Subject:</span>
                  <span className="text-sm">{schedule.subject}</span>
                </div>
              </div>
              <div className="text-sm p-4 space-y-2">
                <div
                  dangerouslySetInnerHTML={{
                    __html: schedule.body.replace(/\n/g, "<br />"),
                  }}
                />
                <div>
                  <img
                    src={mover?.logo_url}
                    alt="Logo"
                    className="max-h-12 object-contain"
                  />
                </div>
                <div className="text-sm text-stone-600">
                  {mover?.business_phone}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="space-y-1">
              <h3 className="text-base font-bold">SMS Preview</h3>
              <p className="text-sm text-stone-600">
                This is how the SMS will look when it is sent.
              </p>
            </div>
            <div className="mt-4 bg-white rounded-xl border border-stone-800 border-8 h-full max-w-[500px] min-h-[600px]">
              <div className="p-4">
                <StatusBar />
              </div>
              <div className="p-4 bg-slate-100">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
                  <span className="text-sm font-semibold">
                    +1 (877) 452-4374
                  </span>
                </div>
              </div>
              <div className="text-sm bg-gray-100 w-3/4 m-4 rounded-xl p-4">
                {schedule.body}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatusBar: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  };

  const SignalBars = () => (
    <div className="flex items-end gap-0.5">
      {[1, 2, 3, 4].map((bar) => (
        <div
          key={bar}
          className={`w-1 bg-black rounded-sm ${
            bar === 1
              ? "h-1"
              : bar === 2
              ? "h-1.5"
              : bar === 3
              ? "h-2"
              : "h-2.5"
          }`}
        />
      ))}
    </div>
  );

  const BatteryIcon = () => (
    <div className="flex items-center gap-2">
      <div className="relative">
        <div className="w-6 h-3 border border-black rounded-sm">
          <div className="w-4 h-1.5 bg-black rounded-sm m-0.5" />
        </div>
        <div className="absolute -right-0.5 top-1 w-0.5 h-1 bg-black rounded-r-sm" />
      </div>
      <span className="text-xs font-medium">85%</span>
    </div>
  );

  return (
    <div className="flex items-center justify-between w-full text-black text-xs font-medium">
      <div className="flex items-center gap-2">
        <span>{formatTime(currentTime)}</span>
      </div>

      <div className="flex items-center gap-3">
        <SignalBars />
        <BatteryIcon />
      </div>
    </div>
  );
};

export default DripCampaigns;
